// FiltersMenu – portal popover that replaces the old filters sidebar. Hosts the
// full DeviceFiltersPanel plus the "filter by request" control so all device
// filtering lives behind a single toolbar button.

import React, { JSX, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ListFilter, X } from 'lucide-react';
import type { DeviceFiltersValue } from '../../../../types/DeviceListTypes';
import { BTN_SECONDARY } from '../utils/deviceListConstants';
import { DeviceFiltersPanel } from './DeviceFiltersPanel';

const MENU_WIDTH = 360;

interface FiltersMenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  filters: DeviceFiltersValue;
  onChangeFilters: (next: DeviceFiltersValue) => void;
  onCsvFilterIdsChange?: (ids: string[] | null) => void;
  lifecycleFilterEnabled?: boolean;
  onOpenSelectRequest: () => void;
  activeRequestId?: string | null;
  requestFilterIds?: string[] | null;
  onClearRequest?: () => void;
}

export function FiltersMenu(props: FiltersMenuProps): JSX.Element | null {
  const {
    open,
    onClose,
    anchorRef,
    filters,
    onChangeFilters,
    onCsvFilterIdsChange,
    lifecycleFilterEnabled = true,
    onOpenSelectRequest,
    activeRequestId,
    requestFilterIds,
    onClearRequest,
  } = props;

  const [coords, setCoords] = useState({ top: 0, left: 0, width: MENU_WIDTH, maxHeight: 480 });

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) return;

    function updatePosition(): void {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(MENU_WIDTH, window.innerWidth - 24);
      let left = r.right - width;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      const top = r.bottom + 6;
      // Cap the height to the space left below the button so the bottom of the
      // panel (and its internal scrollbar) never runs off-screen.
      const maxHeight = Math.max(240, window.innerHeight - top - 12);
      setCoords({ top, left, width, maxHeight });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, anchorRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[280] bg-slate-900/25 dark:bg-black/50" role="presentation" onClick={onClose} />
      <div
        className="fixed z-[290] flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left text-gray-800 shadow-2xl ring-1 ring-black/5 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        style={{ top: coords.top, left: coords.left, width: coords.width, maxHeight: coords.maxHeight }}
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2">
          <div className="text-xs font-semibold text-gray-800 dark:text-slate-200">Filters</div>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            onClick={onClose}
            aria-label="Close filters"
            title="Close"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3">
          <div className="shrink-0">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              By request
            </div>
            {activeRequestId ? (
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-[11px] text-violet-900 dark:border-violet-700/50 dark:bg-violet-950/40 dark:text-violet-200">
                <span className="font-medium text-violet-800/80 dark:text-violet-300/80">Request</span>
                <span className="min-w-0 flex-1 truncate font-mono text-violet-950 dark:text-violet-100">
                  {activeRequestId}
                </span>
                <span className="tabular-nums text-violet-800/80 dark:text-violet-300/80">
                  ({requestFilterIds ? requestFilterIds.length : '…'})
                </span>
                {onClearRequest && (
                  <button
                    type="button"
                    className="font-medium text-violet-700 hover:text-violet-900 hover:underline dark:text-violet-300 dark:hover:text-violet-100"
                    onClick={onClearRequest}
                  >
                    Show all
                  </button>
                )}
              </div>
            ) : null}
            <button
              type="button"
              className={`${BTN_SECONDARY} inline-flex w-full items-center justify-center gap-1.5`}
              onClick={() => {
                onClose();
                onOpenSelectRequest();
              }}
            >
              <ListFilter className="h-3.5 w-3.5 opacity-70" aria-hidden />
              {activeRequestId ? 'Change request filter' : 'Filter by request'}
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col border-t border-gray-100 pt-2 dark:border-slate-700">
            <DeviceFiltersPanel
              value={filters}
              onChange={onChangeFilters}
              onCsvFilterIdsChange={onCsvFilterIdsChange}
              lifecycleFilterEnabled={lifecycleFilterEnabled}
            />
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
